/**
 * Peace Defender — 30 handcrafted-feeling wave-clear levels across 6 neon biomes.
 *
 * The player pilots a ship through vibrant neon space biomes, destroying
 * incoming asteroids (and occasional enemy scouts) to clear each wave.
 *
 * Progression:
 *   • 6 Biomes × 5 levels = 30 levels
 *   • Every 5th level is a "Boss Wave" (a single large boss asteroid that splits)
 *   • Ships & power-ups scale with level pacing
 *
 * Star rules (uniform, satisfaction-first):
 *   ⭐   Clear the wave.
 *   ⭐⭐  Clear with 1 or more lives remaining.
 *   ⭐⭐⭐ Clear taking zero hits (no lives lost).
 */

export const BIOMES = [
  {
    id: 1, name: 'Neon Nebula', range: [1, 5],
    bg: ['#1a0c3a', '#2b0e5a'],
    accent: '#38f2ff', glow: '#ff5cff',
    tagline: 'A quiet drift through cyan and rose. Ease into the rhythm.',
  },
  {
    id: 2, name: 'Emerald Belt', range: [6, 10],
    bg: ['#062b2a', '#0b4d3c'],
    accent: '#39ffcf', glow: '#8affa1',
    tagline: 'The rocks come faster. Read the arcs, not the chaos.',
  },
  {
    id: 3, name: 'Solar Flare', range: [11, 15],
    bg: ['#3a0f06', '#7a2812'],
    accent: '#ffb43a', glow: '#ff6b3a',
    tagline: 'Scouts enter the field. Pick your targets. Stay centered.',
  },
  {
    id: 4, name: 'Violet Void', range: [16, 20],
    bg: ['#1a0530', '#4a0d70'],
    accent: '#c17dff', glow: '#7d3aff',
    tagline: 'Silent. Faster. Break the field before it breaks you.',
  },
  {
    id: 5, name: 'Crimson Cluster', range: [21, 25],
    bg: ['#2a0410', '#6b0a24'],
    accent: '#ff3a7d', glow: '#ff7d3a',
    tagline: 'Splitters everywhere. Aim small, breathe smaller.',
  },
  {
    id: 6, name: 'Prism Frontier', range: [26, 30],
    bg: ['#04162a', '#0a2f6b'],
    accent: '#7dfaff', glow: '#ffe23a',
    tagline: 'Everything at once. Colors bleeding into peace.',
  },
];

export function biomeForLevel(level) {
  return BIOMES.find(b => level >= b.range[0] && level <= b.range[1]) || BIOMES[0];
}

const NAMES = [
  'First Light',     'Slow Drift',      'Reading the Sky', 'Steady Hand',      'Nebula Passage',
  'Green Silence',   'Belt Weaver',     'Emerald Rush',    'Vector Focus',     'Belt Master',
  'Sun Strike',      'Flare Rider',     'Scout Patrol',    'Coronal Path',     'The Bright Line',
  'Void Whisper',    'Shadow Split',    'Violet Storm',    'Silent Threshold', 'The Deep Purple',
  'Crimson Dawn',    'Split Second',    'Blood Nebula',    'The Divide',       'Crimson Command',
  'Prism Awakens',   'Refracted Path',  'Spectrum Weave',  'Peace Bearer',     'The Final Prism',
];

const INTROS = [
  'Ease in. Rotate, thrust, breathe. The asteroids are patient.',
  'Slow now. Read the arcs.',
  'Pace matters more than aim. Trust it.',
  'Fewer motions. Better ones.',
  'A wider field. Same discipline.',
  'Green light means keep moving. Not rushing.',
  'Weave. Don\'t chase.',
  'They come faster. So does your rhythm.',
  'Aim between the drift, not at it.',
  'Master this belt — the ship is your breath.',
  'Solar noise. Filter it. Read what matters.',
  'Flares don\'t warn. You anticipate.',
  'Scouts arrive. Prioritize what closes on you.',
  'Follow the coronal line — it always ends clean.',
  'The bright line is peace. Cross it steady.',
  'Silence up here. Yours too.',
  'Rocks split when hit. Plan for the second shot.',
  'A storm of violet. Center yourself first.',
  'Almost through. Same steady breath.',
  'The deep purple ends here.',
  'Crimson rising. Small aims.',
  'Every hit spawns two. Every miss costs.',
  'Not chaos. Just density. Read it.',
  'The divide is where mastery lives.',
  'Take command. It was always yours.',
  'A gift of a wave. Feel the colors.',
  'Refract every threat into a path.',
  'Spectrum weaving — your best breathing.',
  'You are the peace this field needs.',
  'Bring it home. Master run.',
];

/**
 * Level factory — deterministic difficulty ramp.
 * @param {number} n level 1..30
 */
function buildLevel(n) {
  const b = biomeForLevel(n);
  const inBiomeT = (n - b.range[0]) / (b.range[1] - b.range[0] || 1);
  const overallT = (n - 1) / 29;
  const lerp = (a, x, t) => a + (x - a) * t;

  // A wave is a total number of "asteroid mass points". Big rocks = 3, medium = 2, small = 1.
  //   Biome 1:   6 → 10 mass  (few rocks, big & slow)
  //   Biome 2:  10 → 14
  //   Biome 3:  14 → 18 (+ scouts)
  //   Biome 4:  18 → 24 (faster)
  //   Biome 5:  22 → 30 (splitters)
  //   Biome 6:  26 → 40 (everything)
  const waveMass = [
    Math.round(lerp(6, 10, inBiomeT)),
    Math.round(lerp(10, 14, inBiomeT)),
    Math.round(lerp(14, 18, inBiomeT)),
    Math.round(lerp(18, 24, inBiomeT)),
    Math.round(lerp(22, 30, inBiomeT)),
    Math.round(lerp(26, 40, inBiomeT)),
  ][b.id - 1];

  // Asteroid drift speed (px/sec at 60fps baseline)
  const rockSpeed    = lerp(38, 92, overallT);
  const rockSpin     = lerp(0.4, 1.6, overallT);

  // Scouts (enemy UFOs) start showing from biome 3 onward
  const hasScouts = b.id >= 3;
  const scoutCount = !hasScouts
    ? 0
    : b.id === 3 ? Math.round(lerp(1, 2, inBiomeT))
    : b.id === 4 ? Math.round(lerp(1, 3, inBiomeT))
    : b.id === 5 ? Math.round(lerp(2, 3, inBiomeT))
    : Math.round(lerp(2, 4, inBiomeT));

  const isBoss = (n % 5 === 0);

  // Starting lives — decreases as game progresses to raise stakes gently
  const lives = n <= 10 ? 4 : n <= 20 ? 3 : 3;

  // Power-up spawn cadence (seconds between drops). Falls off in later levels.
  const powerUpEvery = lerp(12, 22, overallT);

  return {
    id: n,
    biome: b.id,
    name: NAMES[n - 1] || `Level ${n}`,
    intro: INTROS[n - 1] || 'Steady.',
    waveMass,
    rockSpeed,
    rockSpin,
    hasScouts,
    scoutCount,
    isBoss,
    lives,
    powerUpEvery,
    // Star thresholds (kept for reference; actual computation in computeStars below)
    starThresholds: {
      one: 'clear',
      two: 'clear + ≥1 life left',
      three: 'clear + no hits',
    },
  };
}

export const LEVELS = Array.from({ length: 30 }, (_, i) => buildLevel(i + 1));
export const MAX_LEVEL = 30;

/**
 * Star computation for a completed Peace Defender run.
 * @param {object} level   the level config
 * @param {object} run     { cleared:bool, livesLeft:number, hits:number, startingLives:number }
 * @returns 0 | 1 | 2 | 3
 */
export function computeStars(level, run) {
  if (!run.cleared) return 0;
  if ((run.hits ?? 0) === 0) return 3;   // Flawless
  if ((run.livesLeft ?? 0) >= 1) return 2; // Cleared with lives left
  return 1;                                // Just cleared
}

/* ------------------ Ships (planes) ------------------ */

/**
 * Ships are unlocked by BOTH level clear AND focus-point spend.
 *   • levelRequired: campaign level that must be cleared for eligibility
 *   • fpCost: FP the player must own (spent on unlock)
 *
 * Every ship has a distinct visual + gameplay feel:
 *   - color palette (neon body + accent glow)
 *   - fire rate, shot count, thrust profile
 *   - a signature "trait" tag surfaced in the picker UI
 */
export const SHIPS = [
  {
    id: 'arrow',
    name: 'Arrow',
    tagline: 'Balanced starter — clean lines, steady breath.',
    body: '#38f2ff', glow: '#7dfaff', engine: '#ff5cff',
    fireCooldown: 260, shotCount: 1, thrust: 0.13, maxSpeed: 3.4,
    trait: 'Balanced',
    levelRequired: 0, fpCost: 0,
  },
  {
    id: 'comet',
    name: 'Comet',
    tagline: 'Rapid magenta thrower — pace over precision.',
    body: '#ff3ab5', glow: '#ff8ad9', engine: '#ffcf3a',
    fireCooldown: 180, shotCount: 1, thrust: 0.14, maxSpeed: 3.7,
    trait: 'Rapid Fire',
    levelRequired: 5, fpCost: 200,
  },
  {
    id: 'nova',
    name: 'Nova',
    tagline: 'Twin-shot glow of gold — wider net, softer aim.',
    body: '#ffcf3a', glow: '#fff17a', engine: '#ff5cff',
    fireCooldown: 300, shotCount: 2, thrust: 0.12, maxSpeed: 3.3,
    trait: 'Twin Shot',
    levelRequired: 10, fpCost: 500,
  },
  {
    id: 'prism',
    name: 'Prism',
    tagline: 'Three-shot rainbow spread — the field is yours.',
    body: '#c17dff', glow: '#7dfaff', engine: '#ff5cff',
    fireCooldown: 340, shotCount: 3, thrust: 0.12, maxSpeed: 3.4,
    trait: 'Triple Spread',
    levelRequired: 15, fpCost: 1000,
  },
  {
    id: 'phantom',
    name: 'Phantom',
    tagline: 'Violet ghost with a quick dash — feel like light.',
    body: '#7d3aff', glow: '#c17dff', engine: '#38f2ff',
    fireCooldown: 240, shotCount: 1, thrust: 0.17, maxSpeed: 4.0,
    trait: 'Dash Thrust',
    levelRequired: 20, fpCost: 1500,
  },
  {
    id: 'titan',
    name: 'Titan',
    tagline: 'Emerald warhorse — extra life, steel-heavy hits.',
    body: '#39ffcf', glow: '#8affa1', engine: '#ffcf3a',
    fireCooldown: 300, shotCount: 1, thrust: 0.11, maxSpeed: 3.1,
    trait: '+1 Life',
    levelRequired: 25, fpCost: 2500,
    bonusLives: 1,
  },
];

export function shipById(id) {
  return SHIPS.find(s => s.id === id) || SHIPS[0];
}

export const ENDLESS_UNLOCK_LEVEL = 15;
export const MASTER_UNLOCK_LEVEL  = 30;
